/**
 * Authentication Context V2 - Production-ready authentication
 *
 * Security improvements:
 * - Centralized state management (no duplicate instances)
 * - Token refresh queue prevents race conditions
 * - Secure logging (no PII in production)
 * - Proper loading states for better UX
 * - Request deduplication for login/logout
 * - AbortController for cleanup
 * - No hardcoded navigation (component responsibility)
 *
 * Usage:
 * 1. Wrap your app with <AuthProviderV2>
 * 2. Use useAuthV2() hook in components
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios, { AxiosError } from 'axios';
import apiClientV2 from '@/api/clientV2';
import { AUTH_ENDPOINTS, USER_TYPES } from '@/constants/auth';
import type { User, LoginResponse, AuthContextValue, LoginResult, AuthState } from '@/types/auth';

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

function isCompanyUser(user: User | null): boolean {
  return user?.userType === USER_TYPES.USER_COMPANY;
}

export const AuthProviderV2: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    isLoggingIn: false,
    isLoggingOut: false,
  });

  // Refs to prevent duplicate operations
  const isInitializing = useRef(false);
  const loginAbortController = useRef<AbortController | null>(null);

  /**
   * Initialize authentication on mount
   * Checks if user has valid tokens and fetches user data
   */
  const initAuth = useCallback(async () => {
    try {
      // Cookie is sent automatically - just try to fetch user data
      const response = await apiClientV2.get<User>(AUTH_ENDPOINTS.ME);
      const user = response.data;

      if (!isCompanyUser(user)) {
        setState(prev => ({
          ...prev,
          user: null,
          isAuthenticated: false,
          isLoading: false,
        }));
        return;
      }

      setState(prev => ({
        ...prev,
        user,
        isAuthenticated: true,
        isLoading: false,
      }));
    } catch (error) {
      // If 401, user is not authenticated (cookies may be missing/expired)
      setState(prev => ({
        ...prev,
        user: null,
        isAuthenticated: false,
        isLoading: false,
      }));
    }
  }, []);

  /**
   * Initialize auth on mount
   */
  useEffect(() => {
    // Prevent duplicate initialization
    if (isInitializing.current) {
      return;
    }

    isInitializing.current = true;

    // Set a timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      setState(prev => ({
        ...prev,
        user: null,
        isAuthenticated: false,
        isLoading: false,
      }));
      isInitializing.current = false;
    }, 10000); // 10 second timeout

    initAuth().finally(() => {
      clearTimeout(timeout);
      isInitializing.current = false;
    });

    return () => {
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once on mount

  /**
   * Login function
   * Note: Does NOT handle navigation - that's the component's responsibility
   */
  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      // Prevent duplicate login attempts
      if (state.isLoggingIn) {
        return { success: false, error: 'Login já em andamento' };
      }

      // Abort previous login if any
      if (loginAbortController.current) {
        loginAbortController.current.abort();
      }

      loginAbortController.current = new AbortController();
      const signal = loginAbortController.current.signal;

      setState(prev => ({ ...prev, isLoggingIn: true }));

      try {
        const response = await apiClientV2.post<LoginResponse>(
          AUTH_ENDPOINTS.LOGIN,
          {
            email,
            password,
            userType: USER_TYPES.USER_COMPANY,
          },
          { signal }
        );

        if (signal.aborted) {
          return { success: false, error: 'Login cancelado' };
        }

        // Tokens are set in cookies by the server automatically
        const { user: userData } = response.data;

        if (!isCompanyUser(userData)) {
          setState(prev => ({ ...prev, isLoggingIn: false }));
          return {
            success: false,
            error: 'Acesso restrito a usuários de empresa',
          };
        }

        setState(prev => ({
          ...prev,
          user: userData,
          isAuthenticated: true,
          isLoggingIn: false,
        }));

        return { success: true };
      } catch (error: unknown) {
        // Ignore abort errors
        if (axios.isCancel(error)) {
          return { success: false, error: 'Login cancelado' };
        }

        setState(prev => ({ ...prev, isLoggingIn: false }));

        if (error instanceof AxiosError) {
          const errorMessage = error.response?.data?.error || error.response?.data?.message;
          return {
            success: false,
            error: errorMessage || 'Erro ao fazer login. Verifique suas credenciais.',
          };
        }

        return {
          success: false,
          error: 'Erro inesperado ao fazer login',
        };
      } finally {
        loginAbortController.current = null;
      }
    },
    [state.isLoggingIn]
  );

  /**
   * Logout function
   * Note: Does NOT handle navigation - that's the component's responsibility
   */
  const logout = useCallback(async (): Promise<void> => {
    // Prevent duplicate logout attempts
    if (state.isLoggingOut) {
      return;
    }

    setState(prev => ({ ...prev, isLoggingOut: true }));

    try {
      // Cookie is sent automatically to backend
      await apiClientV2.post(AUTH_ENDPOINTS.LOGOUT);
    } catch (error) {
      // Don't throw - we still want to clear local state
    } finally {
      // Clear local state
      // Cookies are cleared by the server
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        isLoggingIn: false,
        isLoggingOut: false,
      });
    }
  }, [state.isLoggingOut]);

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to use authentication context
 * Must be used within AuthProviderV2
 */
export const useAuthV2 = (): AuthContextValue => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuthV2 deve ser usado dentro de AuthProviderV2');
  }

  return context;
};
